/**
 * opora-tool-d4-sample.js — "See a real example" modal for Edge tools.
 *
 * Wires existing sample triggers (.d4-sample-link, [data-opora-sample]) to a modal
 * populated from opora-edge-samples.json for the current tool. Gated behind
 * window.OPORA_FLAGS.sample_output_v1 — when off, triggers keep their original
 * behavior and no modal is attached.
 */
(function (w, d) {
  'use strict';

  function flagOn() { return !!(w.OPORA_FLAGS && w.OPORA_FLAGS.sample_output_v1); }
  if (!flagOn()) return;

  function currentSlug() {
    var zone = d.querySelector('.opora-input-zone');
    if (zone && zone.dataset && zone.dataset.tool) return zone.dataset.tool;
    var meta = d.querySelector('meta[name="opora:tool-slug"]');
    return (meta && meta.content) || '';
  }

  var SAMPLES = null;
  function loadSamples() {
    if (SAMPLES) return Promise.resolve(SAMPLES);
    var assetBase = (function () {
      var s = d.querySelector('script[src*="opora-tool-d4-sample.js"]');
      if (s && s.src) return s.src.replace(/opora-tool-d4-sample\.js.*$/, '');
      return '';
    })();
    var url = (w.OPORA_EDGE_SAMPLES_URL) || (assetBase + 'opora-edge-samples.json');
    return fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { SAMPLES = (j && j.samples) || {}; return SAMPLES; })
      .catch(function () { SAMPLES = {}; return SAMPLES; });
  }

  var STYLE_ID = 'opora-sample-style';
  function ensureStyle() {
    if (d.getElementById(STYLE_ID)) return;
    var css = '' +
      '.opora-sample-overlay{position:fixed;inset:0;background:rgba(20,18,14,.55);z-index:10000;display:flex;align-items:center;justify-content:center;padding:24px;opacity:0;transition:opacity .18s}' +
      '.opora-sample-overlay[data-open="true"]{opacity:1}' +
      '.opora-sample-modal{background:#fff;border-radius:16px;max-width:620px;width:100%;max-height:86vh;overflow:auto;box-shadow:0 24px 64px rgba(0,0,0,.32);font-family:Inter,system-ui,sans-serif}' +
      '.opora-sample-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:22px 24px 14px;border-bottom:1px solid #ece7df;position:sticky;top:0;background:#fff}' +
      '.opora-sample-title{font-size:17px;font-weight:700;color:#28251d;margin:0}' +
      '.opora-sample-sub{font-size:13px;color:#6f6d66;margin:4px 0 0}' +
      '.opora-sample-close{background:none;border:none;font-size:24px;line-height:1;cursor:pointer;color:#6f6d66;padding:2px 6px;border-radius:8px}' +
      '.opora-sample-close:hover{background:#f6f3ee}' +
      '.opora-sample-body{padding:20px 24px 8px;color:#28251d;font-size:14px;line-height:1.6}' +
      '.opora-sample-body .ots-badge{display:inline-block;padding:5px 12px;border-radius:20px;font-size:12px;font-weight:700;letter-spacing:.04em;color:#fff;background:#9a958c}' +
      '.opora-sample-body .ots-badge--warn{background:#C9A24B}.opora-sample-body .ots-badge--ok{background:#7BA05B}.opora-sample-body .ots-badge--bad{background:#B5736B}' +
      '.opora-sample-body .ots-lede{font-size:15px;margin:12px 0 16px}' +
      '.opora-sample-body .ots-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:0 0 18px}' +
      '.opora-sample-body .ots-metric{background:#f6f3ee;border-radius:8px;padding:12px}' +
      '.opora-sample-body .ots-metric-label{display:block;font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#7a7974;margin-bottom:4px}' +
      '.opora-sample-body .ots-metric-val{font-size:20px;font-weight:700;color:#28251d}' +
      '.opora-sample-body .ots-h4{font-size:13px;font-weight:700;margin:16px 0 6px}' +
      '.opora-sample-body .ots-quote{border-left:3px solid #01696f;background:#f1f6f5;margin:0;padding:12px 14px;border-radius:0 8px 8px 0;font-style:italic}' +
      '.opora-sample-body .ots-note{font-size:12px;color:#8a8780;margin:16px 0 4px}' +
      '.opora-sample-foot{padding:14px 24px 22px;display:flex;justify-content:flex-end;gap:10px;position:sticky;bottom:0;background:#fff;border-top:1px solid #ece7df}' +
      '.opora-sample-cta{padding:10px 18px;background:#01696f;color:#fff;border:none;border-radius:9px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit}' +
      '.opora-sample-cta:hover{background:#014f54}' +
      '.opora-sample-close:focus-visible,.opora-sample-cta:focus-visible{outline:2px solid #01696f;outline-offset:2px}' +
      '@media (prefers-reduced-motion:reduce){.opora-sample-overlay{transition:none}}';
    var st = d.createElement('style'); st.id = STYLE_ID; st.textContent = css; d.head.appendChild(st);
  }

  var lastFocus = null;
  function openModal(sample) {
    ensureStyle();
    lastFocus = d.activeElement;
    var overlay = d.createElement('div');
    overlay.className = 'opora-sample-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', sample.title || 'Sample output');
    overlay.innerHTML =
      '<div class="opora-sample-modal">' +
        '<div class="opora-sample-head"><div><p class="opora-sample-title">' + (sample.title || 'Sample output') + '</p>' +
          (sample.subtitle ? '<p class="opora-sample-sub">' + sample.subtitle + '</p>' : '') + '</div>' +
          '<button class="opora-sample-close" type="button" aria-label="Close">&times;</button></div>' +
        '<div class="opora-sample-body">' + (sample.html || '<p>No sample available.</p>') + '</div>' +
        '<div class="opora-sample-foot"><button class="opora-sample-cta" type="button">Try it with your numbers</button></div>' +
      '</div>';
    d.body.appendChild(overlay);
    requestAnimationFrame(function () { overlay.setAttribute('data-open', 'true'); });

    function close() {
      overlay.setAttribute('data-open', 'false');
      setTimeout(function () { overlay.parentNode && overlay.parentNode.removeChild(overlay); }, 200);
      d.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') {
        var f = overlay.querySelectorAll('button');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && d.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    overlay.querySelector('.opora-sample-close').addEventListener('click', close);
    overlay.querySelector('.opora-sample-cta').addEventListener('click', function () {
      close();
      var f = d.querySelector('[id^="opf_"]:not([type="hidden"])');
      if (f && f.focus) f.focus();
      var form = d.getElementById('d4FormZone');
      if (form && form.scrollIntoView) form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    d.addEventListener('keydown', onKey);
    overlay.querySelector('.opora-sample-close').focus();
  }

  function handleTrigger(e) {
    e.preventDefault();
    var slug = currentSlug();
    loadSamples().then(function (samples) {
      var s = samples[slug];
      if (!s) { return; }
      openModal(s);
    });
  }

  function wire() {
    var triggers = d.querySelectorAll('.d4-sample-link, [data-opora-sample]');
    if (!triggers.length) return;
    var slug = currentSlug();
    loadSamples().then(function (samples) {
      var hasSample = !!(samples && samples[slug]);
      for (var i = 0; i < triggers.length; i++) {
        var t = triggers[i];
        if (t.dataset.oporaSampleWired) continue;
        t.dataset.oporaSampleWired = '1';
        if (hasSample) {
          t.addEventListener('click', handleTrigger);
        } else {
          // No sample for this tool — hide the trigger's rail card (or the link) to avoid a dead click.
          var card = t.closest ? t.closest('.d4-rail-card') : null;
          if (card) card.style.display = 'none';
          else t.style.display = 'none';
        }
      }
    });
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', wire);
  else wire();

})(window, document);
